import Cookies from 'js-cookie'
import { COOKIE_MAX_AGE_DAYS } from './constant'

/** js-cookie: numeric `expires` = days (not seconds). Do not use `maxAge` — it is ignored by browsers. */

const COOKIE_OPTIONS = {
    path: '/',
    sameSite: 'lax' as const,
    expires: COOKIE_MAX_AGE_DAYS,
    secure: true,
    // httpOnly: true, // optional
}

/** Auth data returned by login / verify-email / verify-phone */
export interface AuthResponseData {
    token?: string
    membership?: boolean
}

/** Fired on the window after auth cookies are set or cleared (client-only). */
export const AUTH_COOKIES_CHANGED_EVENT = 'auth-cookies-changed'

function emitAuthCookiesChanged(): void {
    if (typeof window === 'undefined') return
    window.dispatchEvent(new CustomEvent(AUTH_COOKIES_CHANGED_EVENT))
}

/**
 * Store auth token and membership after successful login/verify (30-day cookies).
 */
export function setAuthCookies(data: AuthResponseData): void {
    const token = data.token ?? ''
    if (token) {
        Cookies.set('auth_token', token, COOKIE_OPTIONS)
        Cookies.set('membership', String(data.membership), COOKIE_OPTIONS)
        emitAuthCookiesChanged()
    }
}

export function getAuthToken(): string | undefined {
    return Cookies.get('auth_token')
}

export function getMembership(): string | undefined {
    return Cookies.get('membership')
}

/**
 * Temporary token used only for the forgot-password "new password" request.
 * When set, baseQuery uses this instead of auth_token cookie so the user is not logged in.
 * Cleared by baseQuery after the request is prepared.
 */
let resetTokenForNextRequest: string | null = null

export function setResetTokenForNextRequest(token: string | null): void {
    resetTokenForNextRequest = token
}

export function getAndClearResetTokenForNextRequest(): string | null {
    const token = resetTokenForNextRequest
    resetTokenForNextRequest = null
    return token
}

/** Clear auth cookies on logout */
export function clearAuthCookies(): void {
    Cookies.remove('auth_token', { path: '/' })
    Cookies.remove('membership', { path: '/' })
    emitAuthCookiesChanged()
}
/** Set geo cookies from the profile API response (logged-in users). */
export function setGeoCookiesFromProfile(ip: string, country: string, iso_code: string): void {
    Cookies.set('user_ip', ip, COOKIE_OPTIONS)
    Cookies.set('user_country', iso_code, COOKIE_OPTIONS)
    Cookies.set('user_country_code', country, COOKIE_OPTIONS)
}

/** Cookie name for FCM token; preserved on logout so push can be re-associated after next login. */
export const FCM_TOKEN_COOKIE = 'fcm_token'

export function getFcmTokenCookie(): string | undefined {
    return Cookies.get(FCM_TOKEN_COOKIE)
}

export function setFcmTokenCookie(token: string): void {
    Cookies.set(FCM_TOKEN_COOKIE, token, COOKIE_OPTIONS)
}

/** Clear all cookies for the current domain and reload the page (e.g. after logout). Preserves fcm_token. */
export function clearAllCookiesAndReload(homePath: string = '/'): void {
    const all = Cookies.get()
    const fcmToken = all[FCM_TOKEN_COOKIE]
    Object.keys(all).forEach((name) => Cookies.remove(name, { path: '/' }))
    if (fcmToken) {
        Cookies.set(FCM_TOKEN_COOKIE, fcmToken, COOKIE_OPTIONS)
    }
    window.location.href = homePath
}